import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Mic, ArrowRight, Check } from "lucide-react";
import { getProgress, WRITTEN_TARGET_MIN, ORAL_TARGET_MIN } from "../lib/dailyPlan";
import Card from "./atoms/Card";

function useDailyProgress() {
  const [progress, setProgress] = useState(() => getProgress());
  useEffect(() => {
    const id = setInterval(() => setProgress(getProgress()), 30000);
    const onFocus = () => setProgress(getProgress());
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return progress;
}

export default function DailySchedule({ compact = false, className = "" }) {
  const { plan, writtenPct, oralPct, totalPct, writtenDone, oralDone } = useDailyProgress();
  const currentDay = Number(localStorage.getItem("currentDay") || 1);

  if (compact) {
    return (
      <div className={className} style={{ background: "var(--paper-deep)", border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)", padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span className="eyebrow">Today's plan</span>
          <span style={{ fontSize: 12, color: "var(--fg-2)", fontWeight: 600 }}>{totalPct}%</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <MiniBar Icon={BookOpen} label="Written" pct={writtenPct} done={writtenDone} mins={plan.writtenMinutes} target={WRITTEN_TARGET_MIN} color="var(--gold-500)" />
          <MiniBar Icon={Mic}      label="Oral"    pct={oralPct}    done={oralDone}    mins={plan.oralMinutes}    target={ORAL_TARGET_MIN}    color="var(--rust)" />
        </div>
      </div>
    );
  }

  return (
    <Card className={className} style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
        <div>
          <span className="eyebrow">Today's plan</span>
          <h3 className="font-display" style={{ fontSize: 26, fontWeight: 400, lineHeight: 1.15, margin: "4px 0 2px" }}>
            Two hours, <em style={{ color: "var(--gold-500)" }}>same topic.</em>
          </h3>
          <p style={{ color: "var(--fg-3)", fontSize: 13, margin: 0 }}>Morning grammar. Evening speaking. Same lesson, twice.</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <p className="font-display" style={{ fontSize: 36, lineHeight: 1, margin: 0, color: "var(--gold-500)" }}>{totalPct}<span style={{ fontSize: 18, color: "var(--fg-3)" }}>%</span></p>
          <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)", margin: 0 }}>complete</p>
        </div>
      </div>

      <SessionBlock
        Icon={BookOpen}
        when={`Day ${currentDay} · written`}
        title="Read the rule, walk the examples."
        minutes={plan.writtenMinutes}
        target={WRITTEN_TARGET_MIN}
        pct={writtenPct}
        done={writtenDone}
        action={writtenDone ? "Done" : "Start written"}
        to={`/lesson/${currentDay}`}
        color="var(--gold-500)"
      />
      <div style={{ height: 12 }} />
      <SessionBlock
        Icon={Mic}
        when={`Day ${currentDay} · oral`}
        title="Speak the same lesson aloud."
        minutes={plan.oralMinutes}
        target={ORAL_TARGET_MIN}
        pct={oralPct}
        done={oralDone}
        action={oralDone ? "Done" : "Start oral"}
        to={`/oral/${currentDay}`}
        color="var(--rust)"
      />
    </Card>
  );
}

function SessionBlock({ Icon, when, title, minutes, target, pct, done, action, to, color }) {
  return (
    <div style={{ background: "var(--paper-deep)", borderRadius: "var(--radius-md)", padding: 16, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <span style={{ width: 36, height: 36, borderRadius: "var(--radius-md)", background: color, color: "var(--paper)", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={16} strokeWidth={1.6} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)", margin: 0 }}>{when}</p>
          <p style={{ fontWeight: 600, fontSize: 14, color: "var(--fg-1)", margin: "2px 0 0" }}>{title}</p>
        </div>
        {done ? (
          <Check size={20} strokeWidth={1.8} color="var(--sage-dark)" />
        ) : (
          <p style={{ margin: 0, textAlign: "right" }}>
            <span className="font-display" style={{ fontSize: 22, color: "var(--fg-1)" }}>{Math.floor(minutes)}</span>
            <span style={{ fontSize: 11, color: "var(--fg-3)" }}> /{target}m</span>
          </p>
        )}
      </div>
      <div style={{ height: 4, background: "rgba(11,17,29,0.08)", borderRadius: 2, overflow: "hidden", margin: "8px 0 12px" }}>
        <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: color, transition: "width 700ms cubic-bezier(.2,.8,.2,1)" }} />
      </div>
      <Link to={to} style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: done ? "var(--sage-dark)" : "var(--gold-700)", borderBottom: "1px solid currentColor", paddingBottom: 1 }}>
        {done ? <><Check size={12} strokeWidth={1.8} /> {action}</> : <>{action} <ArrowRight size={12} strokeWidth={1.8} /></>}
      </Link>
    </div>
  );
}

function MiniBar({ Icon, label, pct, done, mins, target, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderRadius: "var(--radius-sm)", padding: "8px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <Icon size={12} strokeWidth={1.6} color={done ? "var(--sage-dark)" : "var(--fg-3)"} />
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: done ? "var(--sage-dark)" : "var(--fg-3)" }}>{label}</span>
        <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, color: "var(--fg-3)" }}>{Math.floor(mins)}/{target}m</span>
      </div>
      <div style={{ height: 4, background: "var(--ink-100)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min(100, pct)}%`, background: color, transition: "width 700ms cubic-bezier(.2,.8,.2,1)" }} />
      </div>
    </div>
  );
}
